// apps/frontend/admin-ui/src/date-picker.ts

/** Input types whose native picker `showPicker()` can open. */
const PICKER_TYPES = new Set(['date', 'datetime-local', 'month', 'time', 'week']);

/**
 * Re-entry guard. A date input wrapped INSIDE its `<label>` receives the click
 * twice — once real, once forwarded by the label — and the second
 * `showPicker()` toggles the just-opened picker shut, which reads as "the
 * calendar doesn't work". Markup should use a sibling `<label for=…>` instead,
 * but the guard keeps the helper correct no matter how a page is written.
 */
const RE_ENTRY_MS = 300;
const lastShown = new WeakMap<HTMLInputElement, number>();

/**
 * Opens the native date/time picker when the user clicks anywhere on a date
 * input under `root`, not just the narrow calendar indicator.
 *
 * Delegated on the container so it keeps working across re-renders, and
 * idempotent per element so calling it again on a re-rendered page is safe.
 * `showPicker()` requires user activation — a click supplies it — and throws
 * where it is unsupported or disallowed, so the call is guarded rather than
 * assumed.
 */
export function wireDatePickers(root: HTMLElement): void {
  if (root.dataset.datePickersWired === '1') return;
  root.dataset.datePickersWired = '1';

  root.addEventListener('click', (ev) => {
    const el = (ev.target as HTMLElement | null)?.closest<HTMLInputElement>('input');
    if (!el || !PICKER_TYPES.has(el.type) || el.disabled || el.readOnly) return;

    const now = Date.now();
    if (now - (lastShown.get(el) ?? 0) < RE_ENTRY_MS) return;
    lastShown.set(el, now);

    try {
      el.showPicker?.();
    } catch {
      /* unsupported or blocked — the native indicator still opens it */
    }
  });
}
